// Files the writer opened with the app (`files.open`, host/host.ts): Five Rooms for Windows hands the page a Scrivener
// project when its `.scrivx` is double-clicked in Explorer, a Word document when its `.docx` is, and a `.zip` chosen with
// Open With. Each is read through the host (`readOpened`) and becomes what the Library shows: a Scrivener project or a
// Word document goes to New Project's import (store/importWord.ts), the rest a note saying where it can be opened. They wait here, first come first, until the writer is done with
// them; App takes them while signed in (`takeOpenedFiles`) and sends the window to the Library.
import { useSyncExternalStore } from 'react';
import { unzipSync } from 'fflate';
import type { ScrivenerPackage } from '@writing-suite/core/import';
import type { Host, OpenedItem } from '../host/host.ts';
import { packageFromEntries, packageFromZip } from './importScrivener.ts';
import { wordFileFrom, type WordFile } from './importWord.ts';

export type OpenedFile =
  | { kind: 'scrivener'; key: string; name: string; pkg: ScrivenerPackage }
  | { kind: 'word'; key: string; name: string; file: WordFile }
  | { kind: 'note'; key: string; name: string; text: string };

let queue: readonly OpenedFile[] = [];
const listeners = new Set<() => void>();
let made = 0;

function setQueue(next: readonly OpenedFile[]) {
  queue = next;
  for (const listener of listeners) listener();
}

/** The file waiting for the writer, if any. */
export function useOpenedFile(): OpenedFile | null {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => queue[0] ?? null,
  );
}

/** Done with it: imported, cancelled or read. */
export function dismissOpenedFile(key: string) {
  setQueue(queue.filter((f) => f.key !== key));
}

const extension = (name: string) => name.slice(name.lastIndexOf('.') + 1).toLowerCase();
const packageNote = (name: string, what: string) =>
  `“${name}” ${what} a Five Rooms package. Restoring one, which merges it into your library, works on a Mac or iPad for now.`;

/** What an opened item is, read through the host. Never throws: a file that can't be read becomes a note. */
export async function readOpenedFile(host: Host, item: OpenedItem): Promise<OpenedFile> {
  const key = `${item.id}:${++made}`;
  const note = (text: string): OpenedFile => ({ kind: 'note', key, name: item.name, text });
  try {
    if (item.error) return note(`Couldn’t open “${item.name}”: ${item.error}.`);
    if (item.kind === 'folder') {
      if (extension(item.name) === 'writingsuite') return note(packageNote(item.name, 'is'));
      const entries: [string, Uint8Array][] = [];
      // as a folder picker names them, under the folder's own name (fileMapFrom strips it)
      for (const entry of item.entries ?? []) entries.push([`${item.name}/${entry.path}`, await host.readOpened(item.id, entry.path)]);
      return { kind: 'scrivener', key, name: item.name, pkg: packageFromEntries(entries) };
    }
    switch (extension(item.name)) {
      case 'docx':
        // wordFileFrom throws the Mac's words for a file that isn't Word's, which become the note.
        return { kind: 'word', key, name: item.name, file: wordFileFrom(item.name, await host.readOpened(item.id)) };
      case 'zip': {
        const bytes = await host.readOpened(item.id);
        try {
          return { kind: 'scrivener', key, name: item.name, pkg: packageFromZip(bytes) };
        } catch (e) {
          if (holdsPackage(bytes)) return note(packageNote(item.name, 'holds'));
          throw e;
        }
      }
      default:
        return note(`Five Rooms doesn’t open “${item.name}”.`);
    }
  } catch (e) {
    return note(e instanceof Error && e.message ? e.message : `Couldn’t open “${item.name}”.`);
  }
}

/** A zip of `.writingsuite` packages (Export everything's, or a Mac's package zipped): a manifest at a package's top. */
function holdsPackage(zip: Uint8Array): boolean {
  try {
    const names = Object.keys(unzipSync(zip, { filter: (f) => f.name.endsWith('manifest.json') }));
    return names.some((n) => n === 'manifest.json' || /\.writingsuite\/manifest\.json$/.test(n));
  } catch {
    return false; // packageFromZip says what's wrong with it
  }
}

/** Takes what the writer opened with the app and queues it. True when something was. */
export async function takeOpenedFiles(host: Host): Promise<boolean> {
  if (!host.has('files.open')) return false;
  const items = await host.takeOpened().catch(() => []);
  if (!items.length) return false;
  const read: OpenedFile[] = [];
  for (const item of items) read.push(await readOpenedFile(host, item));
  setQueue([...queue, ...read]);
  return true;
}
